import { useState } from "react";
import { ChevronDown, Database, GitBranch, Info, ShieldCheck } from "lucide-react";

function ConfidenceMeter({ value = 0 }) {
  const pct = Math.round(value * 100);
  const color = value >= 0.75 ? "bg-accent" : value >= 0.4 ? "bg-amber-400" : "bg-red-400";
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
        <span>Confidence</span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted/50">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Chips({ items, icon: Icon, tone = "default" }) {
  if (!items?.length) return null;
  const cls = tone === "verified"
    ? "border-blue-500/50 bg-blue-500/10 text-blue-300"
    : "border-border/60 bg-card/50 text-muted-foreground";
  const iconCls = tone === "verified" ? "text-blue-400" : "text-accent";
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={i} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${cls}`}>
          {Icon && <Icon className={`h-3 w-3 ${iconCls}`} />}
          {it}
        </span>
      ))}
    </div>
  );
}

export function PlanPanel({ plan }) {
  const [whyOpen, setWhyOpen] = useState(false);
  if (!plan) {
    return (
      <div data-testid="chat-plan-panel" className="p-6 text-sm text-muted-foreground">
        Select an answer to inspect its plan — SQL, lineage and confidence.
      </div>
    );
  }
  const sql = plan.executed_sql || plan.sql;
  const boost = plan.confidence_boost;
  const verifiedJoin = boost?.reasons?.some((r) => r.includes("verified join"));

  return (
    <div data-testid="chat-plan-panel" className="space-y-5 p-5 font-mono">
      <div className="flex items-center gap-2">
        <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {plan.intent}
        </span>
      </div>

      <ConfidenceMeter value={plan.confidence || 0} />
      {boost && (
        <div data-testid="chat-confidence-boost" className="flex items-center gap-1.5 rounded-md border border-blue-500/30 bg-blue-500/5 px-2.5 py-1.5 text-[10px] text-blue-300">
          <ShieldCheck className="h-3 w-3" />
          +0.1 confidence · {boost.reasons.join(" · ")}
        </div>
      )}

      {sql ? (
        <div>
          <div className="mb-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">SQL</div>
          <pre data-testid="chat-plan-sql" className="max-h-56 overflow-auto rounded-lg border border-border/60 bg-background/60 p-3 text-[11px] leading-relaxed text-foreground/90 whitespace-pre-wrap">
{sql}
          </pre>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No SQL — answered from schema metadata.</p>
      )}

      {plan.tables_used?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Tables</div>
          <Chips items={plan.tables_used} icon={Database} />
        </div>
      )}
      {plan.edges_used?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Joins</div>
          <Chips items={plan.edges_used} icon={GitBranch} tone={verifiedJoin ? "verified" : "default"} />
        </div>
      )}
      {plan.assumptions?.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Assumptions</div>
          <ul className="space-y-1 text-[11px] text-muted-foreground">
            {plan.assumptions.map((a, i) => <li key={i}>• {a}</li>)}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-border/60">
        <button
          data-testid="chat-why-toggle"
          onClick={() => setWhyOpen((o) => !o)}
          className="flex w-full items-center justify-between px-3 py-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
        >
          <span className="inline-flex items-center gap-2"><Info className="h-3.5 w-3.5 text-accent" /> Why this answer?</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${whyOpen ? "rotate-180" : ""}`} />
        </button>
        {whyOpen && (
          <div className="space-y-3 border-t border-border/40 p-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Entities resolved</div>
              <ul className="mt-1.5 space-y-1.5 text-[11px]">
                {(plan.resolved || []).map((r, i) => (
                  <li key={i} className="text-muted-foreground">
                    <span className="text-foreground">{r.display_name}</span>
                    <span className="text-[10px]"> · {r.table_fqn}{r.column_name ? `.${r.column_name}` : ""} · {r.score}</span>
                  </li>
                ))}
                {!(plan.resolved || []).length && <li className="text-muted-foreground">—</li>}
              </ul>
            </div>
            {plan.metric_defs_used?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Metric definitions used</div>
                <Chips items={plan.metric_defs_used} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
